import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { HomeScreen } from './components/home/HomeScreen';
import { AppShell, type View } from './components/layout/AppShell';

/**
 * The two tools are loaded on demand. The image inspector pulls in a metadata-reading library
 * that the text tool has no use for, so someone who only came to clean text never downloads it.
 */
const FileWorkspace = lazy(() =>
  import('./components/image/FileWorkspace').then((module) => ({ default: module.FileWorkspace })),
);
const TextWorkspace = lazy(() =>
  import('./components/text/TextWorkspace').then((module) => ({ default: module.TextWorkspace })),
);

const VIEWS: readonly View[] = ['home', 'image', 'text'];

function viewFromHash(): View {
  const hash = window.location.hash.replace('#', '');
  return (VIEWS as readonly string[]).includes(hash) ? (hash as View) : 'home';
}

/**
 * Routing is a hash and three components. There is no router dependency because there is nothing
 * to route: no data loading, no server, no URLs that mean anything to anyone but this tab.
 */
function LoadingTool() {
  return (
    <p className="text-muted py-16 text-center text-sm" role="status">
      Loading…
    </p>
  );
}

export function App() {
  const [view, setView] = useState<View>(viewFromHash);

  useEffect(() => {
    const onHashChange = () => setView(viewFromHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const navigate = useCallback((next: View) => {
    window.location.hash = next === 'home' ? '' : next;
    setView(next);
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <AppShell view={view} onNavigate={navigate}>
      {view === 'home' ? <HomeScreen onNavigate={navigate} /> : null}
      <Suspense fallback={<LoadingTool />}>
        {view === 'image' ? <FileWorkspace /> : null}
        {view === 'text' ? <TextWorkspace /> : null}
      </Suspense>
    </AppShell>
  );
}
